// What Dados gerais says and shows, from the answers the lib holds. No rule
// lives here: which question comes next, how many remain, what a typed date
// means and how old that makes her are poweryourflow-lib's (DADOS,
// resumeAt, progress, readBirthDate, ageOn). This file only puts words on
// them.
//
// SPEC-31: in the app's language, which the screen passes as `words`; the
// admin, which shows her answers in PT whatever its own language (SPEC-38),
// leaves it out. Her values (sport, level, club, load, competition) are
// worded by the copy, which keeps them in PT.
//
// SPEC-55: the laterality question and its answers come from the lib's
// table, worded in the language by `side` (src/profile/lateralityWords.ts),
// PT when it is left out. Her own typed words show as typed.

import { COMPETITION_TYPES, ageOn, isLateralityKind, lateralityQuestionFor } from "poweryourflow-lib";
import type { LateralityQuestion } from "poweryourflow-lib";

import type { Copy } from "../i18n/language";
import { copy } from "../i18n/pt-PT";
import { lateralityWords } from "../profile/lateralityWords";
import type { LateralityWords } from "../profile/lateralityWords";
import type { Answers } from "../schema/types";

const PT_SIDE = lateralityWords("pt");

/** The device's calendar date, YYYY-MM-DD: an age is counted on the day she sees, not in UTC. */
export function localToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** YYYY-MM-DD as dd/mm/aaaa. */
export function displayDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
}

const asText = (value: unknown): string => (typeof value === "string" ? value : "");

/** Her first name as she gave it, or nothing yet. */
export const firstNameOf = (answers: Answers): string => asText(answers.firstName);

/** Her age today from the stored birth date, or undefined. */
export function ageOf(answers: Answers, today: string): number | undefined {
  const birthDate = asText(answers.birthDate);
  return birthDate ? ageOn(birthDate, today) : undefined;
}

/** The screens of Dados gerais that the conversation asks, by question id. */
export type Asked =
  | "firstName"
  | "birthDate"
  | "modality"
  | "position"
  | "laterality"
  | "level"
  | "club"
  | "trainingsPerWeek"
  | "competition";

/** The laterality question her modality asks, the one given two screens earlier (SPEC-55). */
export const lateralityQuestionOf = (answers: Answers): LateralityQuestion => lateralityQuestionFor(asText(answers.modality));

/**
 * Lena's bubbles before a question, worded with what she has said so far.
 * `opening`: the first question of this conversation, which Lena greets her before.
 */
export function questionLines(
  question: Asked,
  answers: Answers,
  today: string,
  opening = false,
  words: Copy = copy,
  side: LateralityWords = PT_SIDE,
): readonly string[] {
  const lines =
    question === "laterality"
      ? [side.question(lateralityQuestionOf(answers).kind)]
      : askLines(question, answers, today, words.profileOnboarding);
  return opening ? [words.profileOnboarding.greeting, ...lines] : lines;
}

function askLines(question: Exclude<Asked, "laterality">, answers: Answers, today: string, text: Copy["profileOnboarding"]): readonly string[] {
  switch (question) {
    case "firstName":
      return text.firstName;
    case "birthDate":
      return text.birthDate(firstNameOf(answers));
    case "modality":
      return text.modality(ageOf(answers, today) ?? 0);
    case "position":
      return text.position;
    case "level":
      return text.level;
    case "club":
      return text.club;
    case "trainingsPerWeek":
      return text.load;
    case "competition":
      return text.competition;
  }
}

/** Her answer to a question, as her bubble and the summary show it. */
export function answerLine(question: Asked, answers: Answers, words: Copy = copy, side: LateralityWords = PT_SIDE): string {
  const text = words.profileOnboarding;
  const value = answers[question];
  switch (question) {
    case "birthDate":
      return displayDate(asText(value));
    case "laterality": {
      const answer = asText(value);
      if (answer === "") return "";
      // Stored with the kind of question that asked it; the modality says which only for a run cut between the two.
      const kind = isLateralityKind(answers.lateralityKind) ? answers.lateralityKind : lateralityQuestionOf(answers).kind;
      return side.answer({ kind, value: answer });
    }
    case "club":
      return asText(value).trim() === "" ? text.noClub : asText(value);
    case "trainingsPerWeek":
      return text.loadAnswer(Number(answers.trainingsPerWeek), Number(answers.hoursPerWeek));
    case "competition": {
      const type = COMPETITION_TYPES.find((option) => option === value);
      return type ? text.competitionOptions[type].title : "";
    }
    default:
      return asText(value);
  }
}

/** The labels of the rows below her name, the summary's and Perfil's. */
interface DetailLabels {
  birth: string;
  modality: string;
  position: string;
  laterality: string;
  level: string;
  club: string;
  load: string;
  competition: string;
}

/** Her answers after the first name, as rows, in the cards' order (SPEC-18 summary, SPEC-17 Dados gerais, SPEC-55 Lateralidade). */
function detailRows(
  answers: Answers,
  today: string,
  labels: DetailLabels,
  words: Copy,
  side: LateralityWords,
): { label: string; value: string }[] {
  const birth = asText(answers.birthDate);
  const value = (question: Asked) => answerLine(question, answers, words, side);
  return [
    { label: labels.birth, value: birth ? words.profileOnboarding.summary.birthValue(displayDate(birth), ageOf(answers, today) ?? 0) : "" },
    { label: labels.modality, value: value("modality") },
    { label: labels.position, value: value("position") },
    { label: labels.laterality, value: value("laterality") },
    { label: labels.level, value: value("level") },
    { label: labels.club, value: value("club") },
    { label: labels.load, value: value("trainingsPerWeek") },
    { label: labels.competition, value: value("competition") },
  ];
}

/** The summary card's rows, in the card's order. */
export function summaryRows(answers: Answers, today: string, words: Copy = copy, side: LateralityWords = PT_SIDE): { label: string; value: string }[] {
  const { summary } = words.profileOnboarding;
  return [{ label: summary.name, value: answerLine("firstName", answers, words) }, ...detailRows(answers, today, summary, words, side)];
}

/** Perfil's eight Dados gerais rows (SPEC-17, SPEC-55); « — » for one she has not answered yet, Lateralidade on an older account. */
export function profileRows(answers: Answers, today: string, words: Copy = copy, side: LateralityWords = PT_SIDE): { label: string; value: string }[] {
  const rows = detailRows(answers, today, words.profile.rows, words, side);
  return rows.map((row) => (row.value === "" ? { ...row, value: words.profile.empty } : row));
}
